import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Vehicle } from '@hero/schema';
import { useVehicleListApi } from '@hero/api-hooks';
import { PlusIcon, SearchIcon, TruckIcon } from '@hero/web-icons';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../../ui/Sheet/Sheet';
import { Button } from '../../../ui/Button/Button';
import {
  AvailabilityChip,
  formatPlateForDisplay,
  vehicleStatusChip,
} from '../../../app/Select/SelectAsync/selectorEntityRowUtils';
import { VehicleEditModal } from '../../Vehicle/Modal/VehicleEditModal';

export interface OrderVehiclePickerDrawerProps {
  /** Whether the drawer is open. Caller controls visibility. */
  isOpen: boolean;
  /** Close handler — fires on overlay click / esc / explicit cancel. */
  onClose: () => void;
  /**
   * Picked vehicle is passed up to the parent. The drawer does NOT
   * commit anything to the backend on its own; the parent decides
   * whether to persist via `useAssignOrderApi`, hold the value for
   * a multi-step flow, etc.
   */
  onPick: (vehicle: Vehicle) => void;
  /** Header title — defaults to a generic "Pick a vehicle" string. */
  title?: string;
  /** Header subtitle. */
  description?: string;
}

/**
 * Reusable vehicle picker drawer matching the LocationPickerDrawer
 * shell — right-side `Sheet`, header, search, scrollable list,
 * tap-to-select. Hosted by `OrderDriverPickerDrawer` as the
 * follow-up step when the user picks a driver who isn't bound to
 * any vehicle, but it's deliberately written as a generic picker so
 * other order flows can reuse it.
 *
 * The list is driven by `useVehicleListApi({ search })` (the same
 * hook that backs `VehicleSelector`), with a 250ms debounce so the
 * user doesn't get a request per keystroke.
 */
export function OrderVehiclePickerDrawer({
  isOpen,
  onClose,
  onPick,
  title,
  description,
}: OrderVehiclePickerDrawerProps) {
  const { t } = useTranslation(['m-order', 'm-vehicle', 'c-main']);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Reset search when the drawer reopens so a stale query from a
  // previous open doesn't leak into the new context.
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setDebounced('');
      setIsCreateModalOpen(false);
    }
  }, [isOpen]);

  // After the user creates a vehicle from the picker, treat it as
  // their pick — the calling drawer (driver picker, future flows)
  // gets the new Vehicle straight away without an extra tap.
  const handleVehicleCreated = (vehicle: Vehicle) => {
    setIsCreateModalOpen(false);
    onPick(vehicle);
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const { data, isLoading, isFetchingNextPage, hasNextPage, onLoadMore } = useVehicleListApi(
    debounced ? { search: debounced } : {},
    { enabled: isOpen },
  );

  const vehicles = useMemo<Vehicle[]>(
    () => (data ?? []).filter((v): v is Vehicle => !!v),
    [data],
  );

  return (
    <>
      <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
        <SheetContent
          side="right"
          className="flex flex-col p-0 bg-white w-[480px] sm:max-w-[480px]">
          <SheetHeader className="border-b px-6 py-4">
            <SheetTitle>
              {title ??
                t('m-order:drawer.vehicle_picker.title', { defaultValue: 'Assign vehicle' })}
            </SheetTitle>
            <SheetDescription>
              {description ??
                t('m-order:drawer.vehicle_picker.description', {
                  defaultValue: 'Pick the vehicle that will move this load.',
                })}
            </SheetDescription>
          </SheetHeader>

          <div className="px-6 py-3 border-b">
            <div className="relative">
              <SearchIcon className="absolute start-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={t('m-order:drawer.vehicle_picker.search_placeholder', {
                  defaultValue: 'Search vehicles by plate or type…',
                })}
                className="w-full ps-9 pe-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading && vehicles.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">
                {t('c-main:async_operations.loading', { defaultValue: 'Loading…' })}
              </div>
            ) : vehicles.length === 0 ? (
              <div className="p-8 flex flex-col items-center text-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-full bg-gray-100">
                  <TruckIcon className="size-5 text-gray-400" />
                </div>
                <div className="text-sm text-gray-500">
                  {debounced
                    ? t('c-main:status.no_results_found', { defaultValue: 'No results found' })
                    : t('m-vehicle:no_vehicles', { defaultValue: 'No vehicles found' })}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsCreateModalOpen(true)}>
                  <PlusIcon className="w-4 h-4 me-1" />
                  {t('m-order:drawer.vehicle_picker.create_new', {
                    defaultValue: 'Create new vehicle',
                  })}
                </Button>
              </div>
            ) : (
              <ul className="divide-y">
                {vehicles.map(vehicle => {
                  const chip = vehicleStatusChip(t, vehicle.status);
                  const plate = formatPlateForDisplay(vehicle.registration?.plate);
                  const typeLabel = vehicle.classification?.type
                    ? t(`m-vehicle:types.${vehicle.classification.type}`, {
                        defaultValue: String(vehicle.classification.type),
                      })
                    : '';
                  return (
                    <li key={vehicle._id.toString()}>
                      <button
                        type="button"
                        onClick={() => onPick(vehicle)}
                        className="w-full text-start px-6 py-3 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none">
                        <div className="flex items-start gap-3">
                          <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 border border-gray-200">
                            <TruckIcon className="size-4 text-primary" />
                          </div>
                          <div className="min-w-0 flex-1 flex flex-col gap-0.5">
                            <span className="text-sm font-semibold text-gray-900 truncate">
                              {plate || t('m-vehicle:no_plate', { defaultValue: 'No plate' })}
                            </span>
                            {typeLabel && (
                              <span className="text-xs text-gray-500 truncate">{typeLabel}</span>
                            )}
                          </div>
                          <AvailabilityChip label={chip.label} tone={chip.tone} />
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {hasNextPage && (
              <div className="p-3 text-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => !isFetchingNextPage && onLoadMore()}
                  disabled={isFetchingNextPage}>
                  {isFetchingNextPage
                    ? t('c-main:async_operations.loading_more', {
                        defaultValue: 'Loading more…',
                      })
                    : t('c-main:status.load_more', { defaultValue: 'Load more' })}
                </Button>
              </div>
            )}
          </div>

          <div className="border-t p-4 flex items-center justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setIsCreateModalOpen(true)}
              className="text-primary">
              <PlusIcon className="w-4 h-4 me-1" />
              {t('m-order:drawer.vehicle_picker.create_new', {
                defaultValue: 'Create new vehicle',
              })}
            </Button>
            <Button variant="outline" onClick={onClose}>
              {t('c-main:crud_buttons.cancel', { defaultValue: 'Cancel' })}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <VehicleEditModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onVehicleSaved={handleVehicleCreated}
      />
    </>
  );
}
